import { useQuery } from '@tanstack/react-query';
import type { ConversationScope } from '../components/GuardianProvider';
import type { Message } from '../gen/openapi';
import type { ClientChatResponse } from '../types/ClientMessage';
import { useApiClient } from './useApiClient';

/**
 * Converts ClientChatResponse to Message for API requests.
 */
const toApiMessage = (clientMessage: ClientChatResponse): Message => ({
  timestamp: clientMessage.timestamp,
  role: (clientMessage.role || 'assistant') as 'user' | 'assistant',
  content: clientMessage.content,
});

export const useChatTitle = (
  conversationId: string,
  messages: ClientChatResponse[] = [],
  scope: ConversationScope = 'chat',
) => {
  const { chat } = useApiClient();
  const apiMessages = messages.map(toApiMessage);
  const payload = {
    conversationTitleRequest: { conversationId, messages: apiMessages },
  };

  // A dashboard analysis never shows up in the conversation list, so it needs no title.
  const isDashboard = scope === 'dashboard';
  return useQuery({
    queryKey: ['chatTitle', conversationId],
    queryFn: () => chat.generateChatTitleApiV1ChatTitlePost(payload).then(({ title }) => title),
    enabled: !!messages.length && !isDashboard,
    staleTime: Infinity,
    gcTime: Infinity,
    meta: { shouldPersist: !isDashboard },
  });
};
